import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import {
    formatDate,
    formatCurrency,
    getMonths,
    addMonths,
    handleAdminError,
    handleAdminSuccess,
} from '../../common/SharedFunctions';
import DateBox from 'devextreme-react/date-box';
//import { BfiNumberSearch } from '../common/LoanComponents';
import { NumericFormat } from 'react-number-format';
import { getLoanTypeName, getLoanExtensionPeriod } from '../common/LoanFunctions';

const ExtensionModal = () => {
    const { isOpen, loanId, extensionId = 0 } = useSelector((state) => state.loan.extensionModal);
    const { dispatch } = store;

    const [loan, set_loan] = useState(null);

    const [id, set_id] = useState(0);
    const [extensionDate, set_extensionDate] = useState(null);
    const [isContractual, set_isContractual] = useState(false);
    const [feePaid, set_feePaid] = useState(null);
    const [newExtensionNumber, set_newExtensionNumber] = useState(1);
    const [previousMaturityDate, set_previousMaturityDate] = useState(null);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const yesNo = [
        { key: false, name: 'No' },
        { key: true, name: 'Yes' },
    ];

    const lastMaturityDate = (loan) => {
        if (!loan) {
            return null;
        }
        const maturityDate = loan.maturityDate;
        if (!maturityDate) {
            return null;
        }
        let maturityDate1 = new Date(maturityDate);
        if (loan.extensions && loan.extensions.length > 0) {
            loan.extensions.forEach((ext) => {
                var extDate = new Date(ext.extensionDate);
                if (extDate > maturityDate1) {
                    maturityDate1 = extDate;
                }
            });
        }
        return maturityDate1;
    };

    // const lastMaturityDatePlusOne = (loan) => {
    //   const lastDate = lastMaturityDate(loan);
    //   if (!lastDate) {
    //     return null;
    //   }
    //   return lastDate.setDate(lastDate.getDate() + 1);
    // };

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
        formColumnMonths: { paddingBottom: '3px', paddingTop: '30px' },
        formRow: { marginBottom: '0' },
        highlightedFormRow: {
            marginBottom: '0',
            color: 'darkred',
            fontWeight: 'bold',
        },
        sectionHeader: { margin: '10px 0' },
        labelDiv: { width: '150px', float: 'left' },
        dataDiv: { width: '400px', float: 'left', fontWeight: 'bold' },
        bfisearch: {
            width: '250px',
            position: 'absolute',
            right: '20px',
            paddingTop: '15px',
        },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            display: 'inline-block',
            width: '100%',
            padding: '5px',
            marginTop: '5px',
            marginBottom: '10px',
        },
        dataSection: {
            display: 'inline-block',
            width: '100%',
            marginBottom: '10px',
        },
    };

    const _handleClose = () => dispatch(loanActions.setExtensionModal({ extensionModal: { isOpen: false } }));

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);
            const model = {
                id,
                extensionDate: new Date(extensionDate).toLocaleDateString('en-us'),
                isContractual,
                feePaid,
            };

            const response = await loanApi.setLoanExtension(loanId, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan extensions saved successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const resetState = () => {
        set_loan(null);
        set_id(0);
        set_extensionDate(null);
        set_isContractual(false);
        set_feePaid(null);
        set_previousMaturityDate(null);
    };

    const loadLoanInfo = async (id) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await loanApi.getLoanExtension(id);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;
        set_loan(loan);

        if (extensionId && extensionId > 0) {
            const idx = loan.extensions.findIndex((a) => a.id === extensionId);
            if (idx > -1) {
                const hb = loan.extensions[idx];
                set_id(extensionId);
                set_extensionDate(new Date(hb.extensionDate));
                set_isContractual(hb.isContractual);
                set_feePaid(hb.feePaid);
                set_newExtensionNumber(idx + 1);
                const ld = idx === 0 ? loan.maturityDate : loan.extensions[idx - 1].extensionDate;
                set_previousMaturityDate(ld);
            }
        } else {
            set_newExtensionNumber(loan.extensions.length + 1);
            const months = getLoanExtensionPeriod(loan?.loanTypeId);
            set_extensionDate(addMonths(lastMaturityDate(loan), months));
            set_previousMaturityDate(lastMaturityDate(loan));
        }
        set_loadingOpen(false);
    };

    useEffect(() => {
        async function loanIdChanged() {
            if (loanId && loanId > 0) {
                await loadLoanInfo(loanId, extensionId);
            } else {
                resetState();
            }
            set_loadingOpen(false);
        }

        loanIdChanged();
    }, [isOpen, loanId, extensionId]);

    if (!isOpen || !loan) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '600px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>Extend Loan</h1>
                                    <div style={styles.bfisearch}>
                                        {/* <BfiNumberSearch loanTypeId={loan?.loanTypeId} onSelect={selectBfiNumber} /> */}
                                    </div>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                            </div>
                            <div
                                className='standard-form no-search'
                                style={{ padding: '0', margin: '0' }}
                            >
                                <form
                                    name='loanExtensionForm'
                                    onSubmit={(event) => onSubmit(event)}
                                >
                                    <div style={{ padding: '0px 7px' }}>
                                        <div style={styles.infoSection}>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={styles.formColumn}
                                                >
                                                    Note Date:
                                                </div>
                                                <div
                                                    className='columns small-12 large-2'
                                                    style={styles.formColumn}
                                                >
                                                    <b>{formatDate(loan.noteDate)}</b>
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={styles.formColumn}
                                                >
                                                    Original Maturity:
                                                </div>
                                                <div
                                                    className='columns small-12 large-8'
                                                    style={styles.formColumn}
                                                >
                                                    <b>{formatDate(loan.maturityDate)}</b>
                                                    &nbsp;&nbsp;&nbsp;
                                                    {getMonths(loan.noteDate, loan.maturityDate)}
                                                </div>
                                            </div>
                                            {loan.extensions && loan.extensions.length > 0 && (
                                                <div>
                                                    {loan.extensions.map((e, index) => {
                                                        return (
                                                            <div
                                                                key={'ext_' + index}
                                                                className='row'
                                                                style={id === e.id ? styles.highlightedFormRow : styles.formRow}
                                                            >
                                                                <div
                                                                    className='columns small-12 large-4'
                                                                    style={styles.formColumn}
                                                                >
                                                                    Extension {index + 1}
                                                                </div>
                                                                <div
                                                                    className='columns small-12 large-8'
                                                                    style={styles.formColumn}
                                                                >
                                                                    <b>{formatDate(e.extensionDate)}</b>
                                                                    &nbsp;&nbsp;&nbsp;
                                                                    {getMonths(
                                                                        index === 0
                                                                            ? loan.noteDate
                                                                            : loan.extensions[index - 1].extensionDate,
                                                                        index === 0
                                                                            ? loan.maturityDate
                                                                            : loan.extensions[index].extensionDate
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <div style={styles.dataSection}>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='approvalDate'>
                                                        Extension {newExtensionNumber} <span className='req'>*</span>
                                                    </label>
                                                    <DateBox
                                                        id='extensionDate'
                                                        value={extensionDate}
                                                        onValueChange={set_extensionDate}
                                                        required={true}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.formColumnMonths}
                                                >
                                                    {getMonths(previousMaturityDate, extensionDate)}
                                                </div>
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='isContractual'>
                                                        Contractual <span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='isContractual'
                                                        value={isContractual}
                                                        onChange={(event) =>
                                                            set_isContractual(event.target.value === 'true' ? true : false)
                                                        }
                                                        required={true}
                                                    >
                                                        {yesNo.map((fs) => {
                                                            return (
                                                                <option
                                                                    key={fs.key}
                                                                    value={fs.key}
                                                                >
                                                                    {fs.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='feePaid'>
                                                        Fee Paid <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        prefix=''
                                                        id='feePaid'
                                                        name='feePaid'
                                                        required={true}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        value={feePaid}
                                                        suffix={'%'}
                                                        placeholder='%'
                                                        onValueChange={(values) => {
                                                            set_feePaid(values.floatValue);
                                                        }}
                                                        // isAllowed={(values, sourceInfo) => {
                                                        //   const value = values?.floatValue;
                                                        //   return value === undefined || value === null || value > 0;
                                                        // }}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-9'
                                                    style={styles.formColumn}
                                                >
                                                    <div
                                                        style={{
                                                            marginTop: '32px',
                                                        }}
                                                    >
                                                        (% of commitment amount)
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={styles.infoSection}>
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                {getLoanTypeName(loan.loanTypeId)} Loan Information
                                            </h2>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    BFI #: <b>{loan.bfiNumber}</b>
                                                </div>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    Loan #: <b>{loan.loanNumber}</b>
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    Borrower:
                                                </div>
                                                <div
                                                    className='columns small-12 large-9'
                                                    style={styles.formColumn}
                                                >
                                                    <b>{loan.borrowerEntity}</b>
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    Underwriter:
                                                </div>
                                                <div
                                                    className='columns small-12 large-9'
                                                    style={styles.formColumn}
                                                >
                                                    <b>{loan.underwriter}</b>
                                                </div>
                                            </div>
                                            {loan.address && (
                                                <>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <br />
                                                            Address:
                                                        </div>
                                                    </div>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <b>
                                                                {loan.address.street1}
                                                                {loan.address.street2 && <div>{loan.address.street2}</div>}
                                                                {(loan.address.city ||
                                                                    loan.address.state ||
                                                                    loan.address.zipCode) && (
                                                                    <div>
                                                                        {loan.address.city}, {loan.address.state}{' '}
                                                                        {loan.address.zipCode}
                                                                    </div>
                                                                )}
                                                            </b>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default ExtensionModal;
